import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause, RotateCcw, Square, CheckCircle2, Maximize, Minimize, AlertTriangle } from "lucide-react";

// Video player used across Home (intro) and Training Manual modules.
// - `videoUrl` may be a single URL string or an array of URLs. When an array
//   with more than one entry is supplied, the videos play back-to-back as a
//   single module (e.g. Spark Plugs Part 1 + Part 2) before `onComplete` fires.
// - If no video is supplied (module video not yet added by the client),
//   falls back to a simulated placeholder with a play button.
export default function VideoPlaceholder({ moduleId, title, videoUrl, onComplete, completed, testIdPrefix }) {
  const urls = Array.isArray(videoUrl) ? videoUrl.filter(Boolean) : videoUrl ? [videoUrl] : [];
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [partIndex, setPartIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // --- Real video controls ---
  const safePlay = (v) => {
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // Recover from a request aborted by React's dev-mode double-mount by
        // forcing the element to re-fetch its current source cleanly.
        v.load();
        v.play().catch(() => {});
      });
    }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      safePlay(v);
    } else {
      v.pause();
    }
  };

  const handleRewind = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, v.currentTime - 10);
  };

  const handleStop = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    setPlaying(false);
    setVideoProgress(0);
  };

  const handleToggleFullscreen = () => {
    const el = containerRef.current;
    const v = videoRef.current;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }
    if (el?.requestFullscreen) {
      el.requestFullscreen();
    } else if (el?.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (v?.webkitEnterFullscreen) {
      // iOS Safari only supports native fullscreen on the <video> element itself
      v.webkitEnterFullscreen();
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setVideoProgress((v.currentTime / v.duration) * 100);
  };

  const handleEnded = () => {
    setPlaying(false);
    if (partIndex < urls.length - 1) {
      setVideoProgress(0);
      setPartIndex((i) => i + 1);
    } else {
      onComplete && onComplete();
    }
  };

  const handleRetryLoad = () => {
    const v = videoRef.current;
    if (!v) return;
    setHasError(false);
    v.load();
    safePlay(v);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Explicitly reload the source on every mount so a request aborted by
    // React's dev-mode double-invoke (or a prior part swap) doesn't leave
    // the element permanently stuck with a "no supported sources" error.
    setHasError(false);
    v.load();
    if (partIndex > 0) {
      safePlay(v);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partIndex]);

  if (urls.length > 0) {
    return (
      <div className="w-full">
        <div ref={containerRef} className={`relative w-full overflow-hidden bg-black ${isFullscreen ? "h-full" : "aspect-video max-h-[80vh] rounded-lg"}`}>
          <video
            key={urls[partIndex]}
            ref={videoRef}
            src={urls[partIndex]}
            className="w-full h-full object-contain bg-black"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={() => setHasError(true)}
            data-testid={`${testIdPrefix}-video-element-${moduleId}`}
          />
          {hasError && (
            <div
              data-testid={`${testIdPrefix}-video-error-${moduleId}`}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/90 text-center px-4"
            >
              <AlertTriangle className="h-8 w-8 text-amber-400" />
              <p className="text-white text-sm font-medium">This video couldn't be played.</p>
              <button
                onClick={handleRetryLoad}
                data-testid={`${testIdPrefix}-video-retry-${moduleId}`}
                className="text-xs font-semibold bg-denso-red hover:bg-denso-lightred text-white px-4 py-2 rounded-full transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          )}
          {urls.length > 1 && (
            <div
              data-testid={`${testIdPrefix}-video-part-indicator-${moduleId}`}
              className="absolute top-3 left-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full"
            >
              Part {partIndex + 1} of {urls.length}
            </div>
          )}
          {completed && (
            <div className="absolute top-3 right-3 bg-emerald-500/90 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Watched
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/60 to-transparent pt-6 pb-3 px-4 space-y-2">
            <div className="h-1 w-full bg-white/25 rounded-full overflow-hidden">
              <div
                className="h-full bg-denso-lightred transition-all duration-150"
                style={{ width: `${videoProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleRewind}
                data-testid={`${testIdPrefix}-rewind-button-${moduleId}`}
                title="Rewind 10s"
                className="h-9 w-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-200"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={togglePlay}
                data-testid={`${testIdPrefix}-playpause-button-${moduleId}`}
                title={playing ? "Pause" : "Play"}
                className="h-11 w-11 rounded-full bg-denso-red flex items-center justify-center text-white hover:bg-denso-lightred transition-colors duration-200"
              >
                {playing ? <Pause className="h-5 w-5 fill-white" /> : <Play className="h-5 w-5 fill-white ml-0.5" />}
              </button>
              <button
                onClick={handleStop}
                data-testid={`${testIdPrefix}-stop-button-${moduleId}`}
                title="Stop"
                className="h-9 w-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-200"
              >
                <Square className="h-3.5 w-3.5 fill-white" />
              </button>
              <button
                onClick={handleToggleFullscreen}
                data-testid={`${testIdPrefix}-fullscreen-button-${moduleId}`}
                title={isFullscreen ? "Exit fullscreen" : "Enlarge video"}
                className="h-9 w-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-200"
              >
                {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
        {title && (
          <p className="text-xs text-slate-400 mt-2 text-center">
            {title}
            {urls.length > 1 ? ` — Part ${partIndex + 1} of ${urls.length}` : ""}
          </p>
        )}
      </div>
    );
  }

  return <PlaceholderSimulation
    moduleId={moduleId}
    title={title}
    onComplete={onComplete}
    completed={completed}
    testIdPrefix={testIdPrefix}
  />;
}

// Simulated "watch" experience for modules that don't have a real video yet.
function PlaceholderSimulation({ moduleId, title, onComplete, completed, testIdPrefix }) {
  const [playing, setPlaying] = useState(false);
  const [simProgress, setSimProgress] = useState(completed ? 100 : 0);
  const intervalRef = useRef(null);
  const SIM_DURATION_MS = 6000;

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const handlePlay = () => {
    if (completed) return;
    setPlaying(true);
    const startedAt = Date.now();
    intervalRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - startedAt) / SIM_DURATION_MS) * 100);
      setSimProgress(pct);
      if (pct >= 100) {
        clearTimer();
        setPlaying(false);
        onComplete && onComplete();
      }
    }, 100);
  };

  return (
    <div className="w-full">
      <div className="relative aspect-video w-full max-h-[80vh] rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center group">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-black" />
        <div className="relative z-10 flex flex-col items-center gap-3 px-4 text-center">
          {completed ? (
            <CheckCircle2 className="h-14 w-14 text-emerald-400" data-testid={`${testIdPrefix}-completed-icon-${moduleId}`} />
          ) : (
            <button
              onClick={handlePlay}
              disabled={playing}
              data-testid={`${testIdPrefix}-play-button-${moduleId}`}
              className="h-16 w-16 rounded-full bg-white/10 border border-white/30 flex items-center justify-center hover:bg-denso-red hover:border-denso-red transition-all duration-200 disabled:opacity-70"
            >
              <Play className="h-7 w-7 text-white fill-white ml-0.5" />
            </button>
          )}
          <p className="text-white/70 text-xs uppercase tracking-[0.2em] font-semibold">
            {completed ? "Module Watched" : playing ? "Playing Video..." : "Video Placeholder"}
          </p>
          <p className="text-white text-sm font-medium">{title}</p>
        </div>
        {playing && (
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/10">
            <div
              className="h-full bg-denso-lightred transition-all duration-100"
              style={{ width: `${simProgress}%` }}
            />
          </div>
        )}
      </div>
      {!completed && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Real training video will appear here once added. Click play to simulate watching.
        </p>
      )}
    </div>
  );
}
